import type { Constructable } from 'bz-utilities/ts';

import type { IViewModel } from 'mobx-utils';
import React from 'react';

export class ContextNotProvidedError extends Error {
  constructor(name: string) {
    super(`[${name}] - Context has not been provided`);
  }
}

const createContext = function <T>(name: string): [React.Context<T>, () => T] {
  const symbol = Symbol(name);
  const context = React.createContext<T | symbol>(symbol);
  context.displayName = name;

  const useContext = (): T => {
    const value = React.useContext(context);

    if (value === symbol) {
      throw new ContextNotProvidedError(name);
    }

    return value as T;
  };

  return [context as React.Context<T>, useContext];
};

//Optional context can hide missing providers. Only use it when absence is a valid state.
export const createOptionalContext = function <T>(name: string, fallback: T) {
  const symbol = Symbol(name);
  const context = React.createContext<T | symbol>(symbol);
  context.displayName = name;

  const useContextStrict = (): T => {
    const value = React.useContext(context);

    if (value === symbol) {
      throw new ContextNotProvidedError(name);
    }

    return value as T;
  };

  const useOptionalContext = (): T => {
    const value = React.useContext(context);
    if (value === symbol) {
      if (process.env.NODE_ENV == 'development') {
        console.warn(`[${name}] - Context has not been provided, using fallback.`);
      }

      return fallback;
    }
    return value as T;
  };

  return [context as React.Context<T>, useContextStrict, useOptionalContext] as const;
};

export const createContextForDispatch = function <T>(name: string) {
  return createContext<React.Dispatch<T>>(name);
};

export const createContextForClass = function <T extends object>(clazz: Constructable<T>) {
  return createContext<T>(`${clazz.name}`);
};

export const createContextForClassOptional = function <T extends object>(clazz: Constructable<T>) {
  return createContext<T | undefined>(`${clazz.name}`);
};

export const createContextForClassArray = function <T extends object>(clazz: Constructable<T>) {
  return createContext<T[]>(`${clazz.name}Array`);
};

export const createContextForEntry = function <T extends object, K extends keyof T>(
  clazz: Constructable<T>,
  key: K
) {
  return createContext<T[K]>(`${clazz.name}[${String(key)}]`);
};

export default createContext;
